import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { ReactElement, useState } from 'react'
import { Tooltip } from '../Tooltip'
/**
 * Component to render a cell with an anchor tag that opens the link in a new tab if the user is holding the ctrl or cmd key
 * @param href - String with the url to redirect to. If the user is holding the ctrl or cmd key, the link will open in a new tab
 * @param children - ReactElement to render inside the cell. It should be the same content that would be rendered with table cell prop. Eg. cell: (props) => (<span>{props.value}</span>)
 * @param shouldPreventDefault - Optional boolean to prevent the default behavior of the click event. Default is true, eg. Contact drawer opens in the same page without navigation
 * @param tooltipMessage - Optional string with the message to show in the tooltip
 * @param clickHandler - Optional function to handle the click event on the cell. If the user is not holding the ctrl or cmd key, the click event will be prevented
 * @returns ReactElement
 */
export function AnchorCell({
  href,
  children,
  shouldPreventDefault = true,
  tooltipMessage,
  clickHandler,
}: {
  href: string
  children: ReactElement
  shouldPreventDefault?: boolean
  tooltipMessage?: string
  clickHandler?: () => void
}): ReactElement {
  const [tooltipContent, setTooltipContent] = useState<string>()
  return (
    <a
      href={href}
      className="flex cursor-pointer items-center justify-between px-2"
      data-opens-drawer={clickHandler ? `` : undefined}
      onClick={(e) => {
        if (shouldPreventDefault && !(e.ctrlKey || e.metaKey)) {
          e.preventDefault()
          clickHandler?.()
        }
      }}
      rel="noreferrer"
    >
      <Tooltip
        className="cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap"
        placement="bottom"
        message={tooltipContent}
        onMouseOver={(event) => {
          if (
            event.currentTarget instanceof HTMLElement &&
            event.currentTarget.scrollWidth > event.currentTarget.clientWidth
          ) {
            setTooltipContent(tooltipMessage)
          } else {
            setTooltipContent(undefined)
          }
        }}
      >
        {children}
      </Tooltip>
      <div className="mr-2 hidden h-8 shrink items-center rounded-lg border border-strong bg-surface-subtle p-2 text-ui-xs text-strong group-hover:flex">
        <span>Open</span>
        <ChevronRightIcon height={16} width={16} />
      </div>
    </a>
  )
}
